/**
 * 대화 선택 ☰ (시안 03 N2). 상단 바 오른쪽 버튼 + 오른쪽에서 밀려 나오는 패널.
 *
 *   [제목 "대화" + 닫기] → 별채대화 줄 → "1:1 대화" → 멤버 다섯 줄(고정 순서) → 멤버 소개 › → 고지
 *
 * - 정렬은 고정: 별채대화, 이모 · 오름 · 손 팀장 · 은별 · 도 대표 (최근순 아님)
 * - 대화가 없는 멤버는 "아직 나눈 대화가 없어요"
 * - ☰ 배지 = 안 읽은 1:1 합계(0이면 숨김). 패널을 열 때마다 다시 셉니다
 * - 1:1 에서 한 얘기는 별채대화·상회칠판에 나오지 않는다는 고지를 늘 둡니다 (CLAUDE.md)
 */
import { useEffect, useRef, useState } from 'react';
import { UnreadDot } from './Brand.jsx';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight, Menu, X } from 'lucide-react';
import { MEMBER_KEYS, MEMBERS } from '@config/members/index.js';
import { Avatar, AvatarStack } from './Avatar.jsx';
import { useNav } from '../stores/nav.js';
import { formatClock } from '../pages/board/boardFeed.js';

function relativeTime(value) {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
    if (minutes < 1) {
        return '지금';
    }
    if (minutes < 60) {
        return `${minutes}분 전`;
    }
    if (minutes < 24 * 60) {
        return formatClock(value);
    }
    return `${date.getMonth() + 1}월 ${date.getDate()}일`;
}

export function SwitcherButton() {
    const [open, setOpen] = useState(false);
    const unread = useNav((state) => state.unreadChats);
    const refreshChats = useNav((state) => state.refreshChats);

    useEffect(() => {
        const controller = new AbortController();
        refreshChats(controller.signal);
        return () => {
            controller.abort();
        };
    }, [refreshChats]);

    return (
        <>
            <button
                type="button"
                aria-label={unread ? `대화 선택, 안 읽은 1:1 ${unread}개` : '대화 선택'}
                onClick={() => setOpen(true)}
                className="relative flex size-touch items-center justify-center text-text-primary"
            >
                <Menu size={22} strokeWidth={1.6} aria-hidden="true" />
                {/* 숫자는 쓰지 않습니다 (시안 04 결정 14). 점 8px, top 10 · right 9 */}
                {unread ? <UnreadDot className="absolute top-2.5 right-[9px]" /> : null}
            </button>
            {open ? <ChatSwitcherPanel onClose={() => setOpen(false)} /> : null}
        </>
    );
}

function ChatSwitcherPanel({ onClose }) {
    const navigate = useNavigate();
    const location = useLocation();
    const panelRef = useRef(null);
    const chats = useNav((state) => state.chats);
    const roomPreview = useNav((state) => state.roomPreview);
    const refreshChats = useNav((state) => state.refreshChats);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        const controller = new AbortController();
        refreshChats(controller.signal);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        panelRef.current?.focus();
        function handleKeyDown(event) {
            if (event.key === 'Escape') {
                onCloseRef.current();
            }
        }
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            controller.abort();
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [refreshChats]);

    const byMember = new Map(chats.map((chat) => [chat.memberKey, chat]));
    const currentChat = /^\/chat\/([a-z]+)/.exec(location.pathname)?.[1] ?? null;
    const inRoom = location.pathname === '/';

    function go(path, state) {
        onClose();
        if (location.pathname !== path) {
            navigate(path, state ? { state } : undefined);
        }
    }

    return createPortal(
        <div className="fixed inset-0 z-50 mx-auto flex max-w-app bg-dim motion-safe:animate-dim-in">
            <button type="button" aria-label="대화 선택 닫기" tabIndex={-1} onClick={onClose} className="flex-1" />
            <section
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label="대화"
                tabIndex={-1}
                className="flex h-full w-[312px] max-w-[85%] flex-col overflow-y-auto bg-surface px-2 pt-2 pb-5 outline-none motion-safe:animate-panel-in"
            >
                <div className="flex items-center justify-between pl-3">
                    <h2 className="m-0 text-[17px] font-bold">대화</h2>
                    <button
                        type="button"
                        aria-label="닫기"
                        onClick={onClose}
                        className="flex size-touch items-center justify-center text-text-primary"
                    >
                        <X size={22} strokeWidth={1.6} aria-hidden="true" />
                    </button>
                </div>

                <button
                    type="button"
                    onClick={() => go('/')}
                    aria-current={inRoom ? 'page' : undefined}
                    className={`mt-1 flex min-h-16 items-center gap-3 rounded-card px-3 py-2 text-left ${
                        inRoom ? 'bg-surface-sunken' : ''
                    }`}
                >
                    <span className="flex w-10 shrink-0 justify-center">
                        <AvatarStack memberKeys={['imo', 'oreum', 'do']} size={20} />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="flex items-center gap-1.5">
                            <span className="text-[15px] font-semibold">별채대화</span>
                            <span className="text-[13px] text-text-secondary">6</span>
                            <span className="ml-auto text-micro font-normal text-text-secondary">지금</span>
                        </span>
                        <span className="truncate text-[13px] text-text-secondary">
                            {roomPreview || '멤버 다섯과 단골이 있는 방'}
                        </span>
                    </span>
                </button>

                <h3 className="m-0 mt-4 mb-1 px-3 text-section text-label-muted">1:1 대화</h3>
                {MEMBER_KEYS.map((memberKey) => {
                    const member = MEMBERS[memberKey];
                    const chat = byMember.get(memberKey);
                    const selected = currentChat === memberKey;
                    return (
                        <button
                            key={memberKey}
                            type="button"
                            onClick={() => go(`/chat/${memberKey}`)}
                            aria-current={selected ? 'page' : undefined}
                            className={`flex h-16 items-center gap-3 rounded-card px-3 text-left ${
                                selected ? 'bg-surface-sunken' : ''
                            }`}
                        >
                            <Avatar memberKey={memberKey} size={40} />
                            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                                <span className="flex items-center gap-2">
                                    <span className="text-[15px] font-semibold">{member.name}</span>
                                    <span className="ml-auto text-micro font-normal text-text-secondary">
                                        {relativeTime(chat?.lastAt)}
                                    </span>
                                </span>
                                <span className="flex items-center gap-2">
                                    <span
                                        className={`min-w-0 flex-1 truncate text-[13px] ${
                                            chat?.lastBody ? 'text-text-secondary' : 'text-label-muted'
                                        }`}
                                    >
                                        {chat?.lastBody ?? '아직 나눈 대화가 없어요'}
                                    </span>
                                    {chat?.unread ? (
                                        <>
                                            <span className="sr-only">안 읽은 말 있음</span>
                                            <UnreadDot className="shrink-0" />
                                        </>
                                    ) : null}
                                </span>
                            </span>
                        </button>
                    );
                })}

                <div className="mt-auto flex flex-col gap-3 pt-4">
                    <button
                        type="button"
                        onClick={() => go('/members', { from: location.pathname })}
                        className="flex min-h-touch items-center gap-2 border-t border-hairline px-3 pt-3 text-left"
                    >
                        <span className="flex flex-1 flex-col">
                            <span className="text-[15px] font-semibold">멤버 소개</span>
                            <span className="text-micro font-normal text-text-secondary">
                                다섯 사람의 이야기와 담당
                            </span>
                        </span>
                        <ChevronRight size={18} strokeWidth={1.6} aria-hidden="true" className="text-text-secondary" />
                    </button>
                    <p className="m-0 px-3 text-micro font-normal text-label-muted">
                        1:1에서 한 얘기는 별채대화와 상회칠판에 나오지 않아요.
                    </p>
                </div>
            </section>
        </div>,
        document.body
    );
}
